import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Layout from "components/Layout";
import EmphasisText from "components/EmphasisText";
import Calendar, { toISODate } from "components/Calendar";
import api from "utils/api";
import { setUser } from "state";
import {
  formatPrice,
  priceForPaymentMethod,
} from "constants/massages";
import {
  captureLeonardoRegistrationSource,
} from "utils/registration";
import {
  clearLeonardoBookingDraft,
  loadLeonardoBookingDraft,
  saveLeonardoBookingDraft,
} from "utils/leonardoBookingDraft";
import { useLeonardoLocale } from "./LeonardoLocaleContext";
import leonardoLogo from "assets/images/nyx_hotels_logo.jpeg";
import flagGermany from "assets/images/Flag_of_Germany.svg";
import flagUnitedKingdom from "assets/images/Flag_of_the_United_Kingdom.svg";
import nikolaiLeftPhoto from "assets/images/sura_vor_nikolai_final.png";
import leonardoMasterPhoto from "assets/images/IMG_1082.jpg";
import nikolaiMiddlePhoto from "assets/images/Homepage_Hero_2.png";
import nikolaiGalleryPhotoA from "assets/images/Homepage_Hero_1.png";
import nikolaiGalleryPhotoB from "assets/images/nikolai_rezeption_2.png";
import nikolaiGalleryPhotoC from "assets/images/nikolai_rezeption_1.png";
import "styles/booking.css";
import "styles/leonardo-nikolai.css";

const BERLIN_TZ = "Europe/Berlin";
const PAGE_PATH = "/leonardo-nyx";
const CAMPAIGN_SOURCE = "leonardo-nyx";
const NYX_BOOKING_START_MINUTES = 7 * 60;
const NYX_BOOKING_END_MINUTES = 20 * 60;
const NYX_THURSDAY_BOOKING_END_MINUTES = 22 * 60;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const GALLERY_IMAGES = {
  left: nikolaiLeftPhoto,
  middle: nikolaiMiddlePhoto,
  a: nikolaiGalleryPhotoA,
  b: nikolaiGalleryPhotoB,
  c: nikolaiGalleryPhotoC,
};

const TESTIMONIALS = [
  { key: "0", service: "wakeup", rating: 4.8 },
  { key: "1", service: "wakeup", rating: 4.6 },
  { key: "2", service: "classic", rating: 4.9 },
  { key: "3", service: "wakeup", rating: 5 },
  { key: "4", service: "classic", rating: 4.7 },
  { key: "5", service: "wakeup", rating: 4.8 },
  { key: "6", service: "classic", rating: 4.5 },
  { key: "7", service: "wakeup", rating: 4.9 },
];

const LeonardoStars = ({ rating }) => (
  <span
    className="leonardo-testimonial-rating"
    aria-label={`${rating} von 5 Sternen`}
  >
    {[0, 1, 2, 3, 4].map((index) => {
      const fill = Math.max(0, Math.min(1, rating - index));
      return (
        <span className="leonardo-star" key={index}>
          <span
            className="leonardo-star-fill"
            style={{ width: `${fill * 100}%` }}
          >
            ★
          </span>
          <span className="leonardo-star-empty">★</span>
        </span>
      );
    })}
  </span>
);

const LeonardoTestimonials = ({ t }) => {
  const loopItems = [...TESTIMONIALS, ...TESTIMONIALS];

  return (
    <section
      className="leonardo-testimonials"
      aria-label={t("testimonials.title")}
    >
      <div className="leonardo-testimonials-head">
        <span>{t("testimonials.kicker")}</span>
        <h2>{t("testimonials.title")}</h2>
      </div>
      <div className="leonardo-testimonial-marquee">
        <div className="leonardo-testimonial-track">
          {loopItems.map((item, index) => (
            <article
              className="leonardo-testimonial-card"
              key={`${item.key}-${index}`}
              aria-hidden={index >= TESTIMONIALS.length}
            >
              <div className="leonardo-testimonial-meta">
                <LeonardoStars rating={item.rating} />
                <span className="leonardo-testimonial-verified">
                  {t("testimonials.verified")}
                </span>
              </div>
              <p>{t(`testimonials.${item.key}.quote`)}</p>
              <div className="leonardo-testimonial-footer">
                <strong>{t(`testimonials.${item.key}.name`)}</strong>
                <span className={`leonardo-service-badge is-${item.service}`}>
                  {item.service === "wakeup"
                    ? t("testimonials.service.wakeup")
                    : t("testimonials.service.classic")}
                </span>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

const LeonardoMasterNote = ({ t }) => (
  <section className="leonardo-master-note" aria-labelledby="leonardo-master-title">
    <img
      src={leonardoMasterPhoto}
      alt={t("master.alt")}
      className="leonardo-master-photo"
      loading="lazy"
    />
    <div>
      <span>{t("master.kicker")}</span>
      <h2 id="leonardo-master-title">{t("master.title")}</h2>
      <blockquote>{t("master.quote")}</blockquote>
      <p>{t("master.name")}</p>
    </div>
  </section>
);

const LeonardoGallery = ({ photos, t, onOpen }) => (
  <div className="leonardo-gallery" aria-label={t("gallery.title")}>
    {photos.map((photo) => (
      <figure key={photo.srcKey}>
        <button
          type="button"
          className="leonardo-gallery-button"
          onClick={() => onOpen(photo)}
          aria-label={t("gallery.enlarge", { alt: photo.alt })}
        >
          <img
            src={photo.src}
            alt={photo.alt}
            loading="lazy"
          />
        </button>
      </figure>
    ))}
  </div>
);

const LeonardoNyxLanguageToggle = ({
  className = "",
  locale,
  setLocale,
  updating,
  t,
}) => (
  <div
    className={["leonardo-lang-toggle", "leonardo-nyx-lang-toggle", className]
      .filter(Boolean)
      .join(" ")}
    role="group"
    aria-label={t("lang.switch")}
  >
    <button
      type="button"
      className={locale === "de" ? "is-active" : ""}
      onClick={() => setLocale("de")}
      lang="de"
      disabled={updating}
      aria-pressed={locale === "de"}
      aria-label="Deutsch"
    >
      <img src={flagGermany} alt="" aria-hidden="true" />
    </button>
    <button
      type="button"
      className={locale === "en" ? "is-active" : ""}
      onClick={() => setLocale("en")}
      lang="en"
      disabled={updating}
      aria-pressed={locale === "en"}
      aria-label="English"
    >
      <img src={flagUnitedKingdom} alt="" aria-hidden="true" />
    </button>
  </div>
);

const guestContactFromUser = (account) => ({
  firstName: account?.firstName || "",
  lastName: account?.lastName || "",
  email: account?.email || "",
});

const guestNameFromContact = (contact) =>
  [contact?.firstName, contact?.lastName].filter(Boolean).join(" ").trim();

const buildHotelServiceAddress = ({ visitLocation, floor, roomNumber, notes }) => ({
  visitLocation,
  visitLocationLabel:
    visitLocation === "wellness" ? "Im Wellness-Bereich" : "Im Hotel",
  floor: visitLocation === "wellness" ? "" : floor,
  roomNumber: visitLocation === "wellness" ? "" : roomNumber,
  street: "Katharinenstraße 29",
  postalCode: "20457",
  city: "Hamburg",
  notes: String(notes || "").trim(),
});

const draftFromState = ({
  selectedOption,
  selectedDate,
  selectedSlot,
  guestDetails,
  paymentMethod,
  bookingPhone,
  guestContact,
}) => ({
  optionId: selectedOption?.id || null,
  selectedDate: selectedDate ? toISODate(selectedDate) : null,
  selectedSlot: selectedSlot || null,
  guestDetails,
  guestContact,
  paymentMethod,
  bookingPhone,
});

const mapAvailabilityError = (responseMessage, t, minimumLeadMinutes = 120) => {
  const message = String(responseMessage || "").toLowerCase();
  if (/2 stunden|mindestens.*2 stunden|2 hours|at least.*2 hours|30 minuten|30 minutes|lead time|vorlauf/i.test(message)) {
    return t("slots.error.leadTime", { minutes: minimumLeadMinutes });
  }
  if (/07:00|7:00|10:00|11:00|20:00|22:00|startzeit|start time/i.test(message)) {
    return t("slots.error.hours");
  }
  return t("slots.error.generic");
};

const berlinTimeMinutes = (iso) => {
  const slotDate = new Date(iso);
  if (Number.isNaN(slotDate.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: BERLIN_TZ,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(slotDate);
  const hour = Number(parts.find((part) => part.type === "hour")?.value);
  const minute = Number(parts.find((part) => part.type === "minute")?.value);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  return hour * 60 + minute;
};

const berlinWeekdayIndex = (date) => {
  const shortWeekday = new Intl.DateTimeFormat("en-US", {
    timeZone: BERLIN_TZ,
    weekday: "short",
  }).format(new Date(date));
  return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(shortWeekday);
};

const isThursdayInBerlin = (date) => Boolean(date) && berlinWeekdayIndex(date) === 4;

const slotStepMinutesForHotelOption = (option) =>
  option?.id === "hotel-wakeup-5"
    ? 10
    : option?.serviceKey === "dr-hoffmann-hotel-15"
      ? 15
      : undefined;

const filterLeonardoSlots = (date, slotList) => {
  if (!Array.isArray(slotList)) return [];
  const endMinutes = isThursdayInBerlin(date)
    ? NYX_THURSDAY_BOOKING_END_MINUTES
    : NYX_BOOKING_END_MINUTES;
  return slotList.filter((iso) => {
    const minutes = berlinTimeMinutes(iso);
    return (
      minutes != null &&
      minutes >= NYX_BOOKING_START_MINUTES &&
      minutes <= endMinutes
    );
  });
};

const LeonardoNyxLanding = () => {
  const {
    t,
    dateLocale,
    hotelOptions,
    galleryLabels,
    locale,
    setLocale,
    updating,
  } = useLeonardoLocale();
  const user = useSelector((s) => s.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const optionDetailRef = useRef(null);
  const restoredDraftRef = useRef(false);
  const galleryHistoryOpenRef = useRef(false);
  const galleryScrollYRef = useRef(0);

  const params = new URLSearchParams(location.search);
  const initialOptionId = params.get("option");
  const initialOption = useMemo(
    () => hotelOptions.find((o) => o.id === initialOptionId) || null,
    [hotelOptions, initialOptionId]
  );

  const [selectedOption, setSelectedOption] = useState(initialOption);
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [guestDetails, setGuestDetails] = useState({
    visitLocation: "hotel",
    floor: "",
    roomNumber: "",
    notes: "",
  });
  const [guestContact, setGuestContact] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });
  const [paymentMethod, setPaymentMethod] = useState("stripe");
  const [bookingPhone, setBookingPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [availabilityErrorRaw, setAvailabilityErrorRaw] = useState(null);
  const [activeGalleryPhoto, setActiveGalleryPhoto] = useState(null);

  const userHasPhone = Boolean(String(user?.phone || "").trim());
  const needsBookingPhone = Boolean(user && selectedOption && !userHasPhone);
  const awaitingEmailVerification = Boolean(user && user.emailVerified === false);
  const bookingGuestContact = user
    ? guestContactFromUser(user)
    : guestContact;
  const bookingGuestName = guestNameFromContact(bookingGuestContact);

  const paidBasePriceCents = selectedOption?.priceCents || 0;
  const effectivePriceCents = priceForPaymentMethod(paidBasePriceCents);

  const leonardoGallery = useMemo(
    () =>
      galleryLabels.map((photo) => ({
        ...photo,
        src: GALLERY_IMAGES[photo.srcKey],
      })),
    [galleryLabels]
  );

  const formatTime = useCallback(
    (iso) =>
      new Date(iso).toLocaleTimeString(dateLocale, {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: BERLIN_TZ,
      }),
    [dateLocale]
  );

  const formatLong = useCallback(
    (date) =>
      date.toLocaleDateString(dateLocale, {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
        timeZone: BERLIN_TZ,
      }),
    [dateLocale]
  );

  const validateGuestDetails = useCallback(() => {
    if (!bookingGuestContact.email.trim()) {
      return t("error.email");
    }
    if (!EMAIL_PATTERN.test(bookingGuestContact.email.trim())) {
      return t("error.emailInvalid");
    }
    if (guestDetails.visitLocation === "wellness") {
      return null;
    }
    if (!String(guestDetails.floor || "").trim()) {
      return t("error.floor");
    }
    if (!String(guestDetails.roomNumber || "").trim()) {
      return t("error.room");
    }
    return null;
  }, [
    bookingGuestContact.email,
    guestDetails.floor,
    guestDetails.roomNumber,
    guestDetails.visitLocation,
    t,
  ]);

  const scrollToOptionDetail = useCallback(() => {
    const target = optionDetailRef.current;
    if (!target) return;
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    target.scrollIntoView({
      behavior: reduceMotion ? "auto" : "smooth",
      block: "start",
    });
  }, []);

  const persistDraft = useCallback(() => {
    if (!selectedOption) return;
    saveLeonardoBookingDraft(
      draftFromState({
        selectedOption,
        selectedDate,
        selectedSlot,
        guestDetails,
        guestContact,
        paymentMethod,
        bookingPhone,
      })
    );
  }, [
    selectedOption,
    selectedDate,
    selectedSlot,
    guestDetails,
    guestContact,
    paymentMethod,
    bookingPhone,
  ]);

  useEffect(() => {
    captureLeonardoRegistrationSource({
      source: CAMPAIGN_SOURCE,
      path: PAGE_PATH,
    });
  }, []);

  useEffect(() => {
    if (!selectedOption?.id) return;
    const next = hotelOptions.find((option) => option.id === selectedOption.id);
    if (next) setSelectedOption(next);
  }, [hotelOptions, selectedOption?.id]);

  useEffect(() => {
    if (restoredDraftRef.current) return;
    restoredDraftRef.current = true;
    const draft = loadLeonardoBookingDraft();
    if (!draft) return;

    const option =
      hotelOptions.find((o) => o.id === draft.optionId) || initialOption;
    if (option) setSelectedOption(option);
    if (draft.selectedDate) {
      const [y, m, d] = draft.selectedDate.split("-").map(Number);
      if (y && m && d) setSelectedDate(new Date(y, m - 1, d));
    }
    if (draft.selectedSlot) setSelectedSlot(draft.selectedSlot);
    if (draft.guestDetails) setGuestDetails((prev) => ({ ...prev, ...draft.guestDetails }));
    if (draft.guestContact) setGuestContact((prev) => ({ ...prev, ...draft.guestContact }));
    if (draft.paymentMethod) setPaymentMethod(draft.paymentMethod);
    if (draft.bookingPhone) setBookingPhone(draft.bookingPhone);
  }, [hotelOptions, initialOption]);

  useEffect(() => {
    persistDraft();
  }, [persistDraft]);

  useEffect(() => {
    if (!user?._id) return undefined;
    let cancelled = false;
    api
      .get("/auth/me")
      .then(({ data }) => {
        if (!cancelled && data?.user) dispatch(setUser({ user: data.user }));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [dispatch, user?._id]);

  useEffect(() => {
    if (!selectedOption?.id) return;
    window.requestAnimationFrame(scrollToOptionDetail);
  }, [selectedOption?.id, scrollToOptionDetail]);

  useEffect(() => {
    if (!selectedOption || !selectedDate) {
      setSlots([]);
      setAvailabilityErrorRaw(null);
      return undefined;
    }
    let cancelled = false;
    setLoadingSlots(true);
    setAvailabilityErrorRaw(null);
    setSelectedSlot(null);
    const availabilityParams = {
      date: toISODate(selectedDate),
      durationMinutes: selectedOption.durationMinutes,
      brand: "dr-hoffmann",
      serviceKey: selectedOption.serviceKey,
      minimumLeadMinutes: selectedOption.minimumLeadMinutes,
      source: CAMPAIGN_SOURCE,
    };
    const slotStepMinutes = slotStepMinutesForHotelOption(selectedOption);
    if (slotStepMinutes) {
      availabilityParams.slotStepMinutes = slotStepMinutes;
    }
    api
      .get("/bookings/availability", {
        params: availabilityParams,
      })
      .then(({ data }) => {
        if (!cancelled) {
          setSlots(filterLeonardoSlots(selectedDate, data?.slots));
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setAvailabilityErrorRaw(err?.response?.data?.message || "generic");
          setSlots([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingSlots(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedDate, selectedOption]);

  const availabilityError = availabilityErrorRaw
    ? mapAvailabilityError(
        availabilityErrorRaw,
        t,
        selectedOption?.minimumLeadMinutes || 120
      )
    : null;

  const handleSelectOption = (option) => {
    setSelectedOption(option);
    setSelectedDate(null);
    setSelectedSlot(null);
    setError(null);
    const nextParams = new URLSearchParams(location.search);
    nextParams.set("option", option.id);
    navigate(`${PAGE_PATH}?${nextParams.toString()}`, { replace: true });
    window.setTimeout(scrollToOptionDetail, 0);
  };

  const updateGuest = (key) => (e) => {
    setGuestDetails((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const updateGuestContact = (key) => (e) => {
    setGuestContact((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const updateVisitLocation = (e) => {
    const visitLocation = e.target.value;
    setGuestDetails((prev) => ({
      ...prev,
      visitLocation,
      ...(visitLocation === "wellness" ? { floor: "", roomNumber: "" } : {}),
    }));
  };

  const submitBooking = async () => {
    setError(null);
    if (!selectedSlot) {
      setError(t("error.pickSlot"));
      return;
    }
    const guestError = validateGuestDetails();
    if (guestError) {
      setError(guestError);
      return;
    }
    if (needsBookingPhone && !String(bookingPhone || "").trim()) {
      setError(t("error.phone"));
      return;
    }

    setSubmitting(true);
    try {
      const serviceAddress = buildHotelServiceAddress(guestDetails);
      serviceAddress.companyName = bookingGuestName;
      const body = {
        serviceKey: selectedOption.serviceKey,
        durationMinutes: selectedOption.durationMinutes,
        paymentMethod,
        priceCents: effectivePriceCents,
        startISO: selectedSlot,
        serviceAddress,
        hoffmannBookingOption: selectedOption.title,
        source: CAMPAIGN_SOURCE,
        preferredLanguage: locale,
        minimumLeadMinutes: selectedOption.minimumLeadMinutes,
        firstName: bookingGuestContact.firstName.trim(),
        lastName: bookingGuestContact.lastName.trim(),
        email: bookingGuestContact.email.trim(),
        guestFirstName: bookingGuestContact.firstName.trim(),
        guestLastName: bookingGuestContact.lastName.trim(),
        guestEmail: bookingGuestContact.email.trim(),
        customer: {
          firstName: bookingGuestContact.firstName.trim(),
          lastName: bookingGuestContact.lastName.trim(),
          email: bookingGuestContact.email.trim(),
        },
      };
      if (selectedOption.offerType) {
        body.offerType = selectedOption.offerType;
      }
      if (selectedOption.personCount) {
        body.personCount = selectedOption.personCount;
      }
      if (selectedOption.serviceDurationMinutes) {
        body.serviceDurationMinutes = selectedOption.serviceDurationMinutes;
      }
      if (needsBookingPhone) {
        body.phone = bookingPhone.trim();
      }

      const { data } = await api.post("/bookings", body);
      const booking = data.booking;

      if (
        paymentMethod === "stripe" &&
        booking?.status === "pending-payment" &&
        booking?.priceCents > 0
      ) {
        const { data: checkout } = await api.post(
          `/payment/checkout/${booking._id}`,
          { priceCents: effectivePriceCents }
        );
        if (!checkout?.url) {
          throw new Error(t("error.checkout"));
        }
        window.location.href = checkout.url;
        return;
      }

      if (user?._id) {
        try {
          const { data: me } = await api.get("/auth/me");
          if (me?.user) dispatch(setUser({ user: me.user }));
        } catch {
          /* ignore */
        }
      }

      clearLeonardoBookingDraft();
      navigate(`/buchen/erfolg?bookingId=${booking._id}`, {
        state: { booking },
      });
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          t("error.bookingFailed")
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitClick = () => {
    persistDraft();
    submitBooking();
  };

  const formComplete =
    Boolean(selectedSlot) &&
    !validateGuestDetails() &&
    (!needsBookingPhone || String(bookingPhone || "").trim());

  const activeGalleryIndex = leonardoGallery.findIndex(
    (photo) => photo.srcKey === activeGalleryPhoto?.srcKey
  );
  const galleryOpen = Boolean(activeGalleryPhoto);

  const openGalleryPhoto = useCallback((photo) => {
    const nextState = {
      ...window.history.state,
      leonardoGalleryEntry: true,
      leonardoGalleryOpen: true,
    };

    if (!galleryHistoryOpenRef.current) {
      if (window.history.state?.leonardoGalleryEntry) {
        window.history.replaceState(nextState, "");
      } else {
        window.history.pushState(nextState, "");
      }
      galleryHistoryOpenRef.current = true;
    }
    setActiveGalleryPhoto(photo);
  }, []);

  const closeGalleryPhoto = useCallback(() => {
    if (galleryHistoryOpenRef.current) {
      window.history.replaceState(
        {
          ...window.history.state,
          leonardoGalleryEntry: true,
          leonardoGalleryOpen: false,
        },
        ""
      );
      galleryHistoryOpenRef.current = false;
    }
    setActiveGalleryPhoto(null);
  }, []);

  const showPreviousGalleryPhoto = useCallback(() => {
    if (activeGalleryIndex < 0) return;
    const previousIndex =
      (activeGalleryIndex - 1 + leonardoGallery.length) % leonardoGallery.length;
    setActiveGalleryPhoto(leonardoGallery[previousIndex]);
  }, [activeGalleryIndex, leonardoGallery]);

  const showNextGalleryPhoto = useCallback(() => {
    if (activeGalleryIndex < 0) return;
    const nextIndex = (activeGalleryIndex + 1) % leonardoGallery.length;
    setActiveGalleryPhoto(leonardoGallery[nextIndex]);
  }, [activeGalleryIndex, leonardoGallery]);

  useEffect(() => {
    if (!activeGalleryPhoto?.srcKey) return;
    const nextPhoto = leonardoGallery.find(
      (photo) => photo.srcKey === activeGalleryPhoto.srcKey
    );
    if (nextPhoto && nextPhoto.alt !== activeGalleryPhoto.alt) {
      setActiveGalleryPhoto(nextPhoto);
    }
  }, [activeGalleryPhoto, leonardoGallery]);

  useLayoutEffect(() => {
    if (!galleryOpen) return undefined;
    galleryScrollYRef.current = window.scrollY || window.pageYOffset || 0;
    const previousScrollRestoration = window.history.scrollRestoration;
    const { style } = document.body;
    const htmlStyle = document.documentElement.style;
    const previousOverflow = style.overflow;
    const previousHtmlOverflow = htmlStyle.overflow;

    window.history.scrollRestoration = "manual";
    style.overflow = "hidden";
    htmlStyle.overflow = "hidden";

    return () => {
      const scrollY = galleryScrollYRef.current;
      style.overflow = previousOverflow;
      htmlStyle.overflow = previousHtmlOverflow;
      window.scrollTo(0, scrollY);
      window.history.scrollRestoration = previousScrollRestoration;
    };
  }, [galleryOpen]);

  useEffect(() => {
    const handlePopState = () => {
      if (!galleryHistoryOpenRef.current) return;
      galleryHistoryOpenRef.current = false;
      setActiveGalleryPhoto(null);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    if (!activeGalleryPhoto) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "ArrowLeft") {
        showPreviousGalleryPhoto();
      } else if (event.key === "ArrowRight") {
        showNextGalleryPhoto();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeGalleryPhoto, showNextGalleryPhoto, showPreviousGalleryPhoto]);

  const submitLabel = submitting
    ? t("submit.processing")
    : paymentMethod === "stripe"
      ? t("submit.pay")
      : awaitingEmailVerification
        ? t("submit.reserve")
        : t("submit.confirm");

  return (
    <Layout
      bodyClass="leonardo-booking-page"
      renderNavLanguageToggle={() => (
        <LeonardoNyxLanguageToggle
          locale={locale}
          setLocale={setLocale}
          updating={updating}
          t={t}
        />
      )}
    >
      <section className="booking-page leonardo-booking-page">
        <div className="leonardo-brand">
          <img
            src={leonardoLogo}
            alt="NYX Hotels by Leonardo Hotels"
            className="leonardo-brand-logo"
          />
        </div>

        <LeonardoTestimonials t={t} />
        <LeonardoMasterNote t={t} />

        <LeonardoGallery photos={leonardoGallery} t={t} onOpen={openGalleryPhoto} />

        <h1 className="booking-title">
          <EmphasisText text={t("page.title")} />
        </h1>
        <p className="booking-intro leonardo-intro">
          {t("page.intro")}
          <br />
          <span className="leonardo-intro-highlight">{t("page.introHighlight")}</span>{" "}
          {t("page.introHours")}
        </p>

        <div className="option-grid leonardo-option-grid">
          {hotelOptions.map((option) => {
            const isSelected = selectedOption?.id === option.id;
            return (
              <article
                key={option.id}
                className={`option-card leonardo-offer-card ${
                  option.featured ? "leonardo-offer-card--featured" : ""
                } ${isSelected ? "is-selected" : ""}`}
              >
                <div className="option-card-content leonardo-offer-body">
                  <div className="leonardo-offer-heading">
                    <h3>{option.title}</h3>
                    <p className="option-subtitle">{option.subtitle}</p>
                  </div>
                  <div className="leonardo-price-row">
                    <strong className="option-price">{option.price}</strong>
                    <span className="option-price-note">{option.priceNote}</span>
                  </div>
                  <p className="leonardo-offer-description">{option.description}</p>
                </div>
                <div className="option-actions">
                  <button
                    type="button"
                    className={isSelected ? "btn-outline" : "btn-primary"}
                    onClick={() => handleSelectOption(option)}
                  >
                    {isSelected ? t("option.selected") : t("option.select")}
                  </button>
                </div>
              </article>
            );
          })}
        </div>

        {activeGalleryPhoto && (
          <div
            className="leonardo-gallery-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={activeGalleryPhoto.alt}
          >
            <button
              type="button"
              className="leonardo-gallery-close"
              onClick={closeGalleryPhoto}
              aria-label={t("gallery.close")}
            >
              ×
            </button>
            <button
              type="button"
              className="leonardo-gallery-nav leonardo-gallery-nav--prev"
              onClick={(e) => {
                e.stopPropagation();
                showPreviousGalleryPhoto();
              }}
              aria-label={t("gallery.prev")}
            >
              ‹
            </button>
            <img
              src={activeGalleryPhoto.src}
              alt={activeGalleryPhoto.alt}
              onClick={(e) => e.stopPropagation()}
            />
            <button
              type="button"
              className="leonardo-gallery-nav leonardo-gallery-nav--next"
              onClick={(e) => {
                e.stopPropagation();
                showNextGalleryPhoto();
              }}
              aria-label={t("gallery.next")}
            >
              ›
            </button>
          </div>
        )}

        {selectedOption && (
          <div className="option-detail" ref={optionDetailRef}>
            <div className="booking-step">
              <div className="step-head">
                <h2>
                  <EmphasisText
                    text={t("step.dateTime", { title: selectedOption.title })}
                  />
                </h2>
                <span className="booking-step-number">01</span>
              </div>
              <Calendar
                selectedDate={selectedDate}
                onSelect={setSelectedDate}
                minDate={new Date()}
                maxDate={new Date(Date.now() + 60 * 24 * 60 * 60 * 1000)}
                weekdaysOnly={false}
                onlyWeekday={-1}
                closedWeekday={-1}
                dateLocale={dateLocale}
                labels={{
                  prev: t("calendar.prev"),
                  next: t("calendar.next"),
                  closedDay: t("calendar.closedDay"),
                }}
              />
              {selectedDate && (
                <>
                  <p className="step-summary leonardo-slot-intro" />
                  {loadingSlots ? (
                    <p className="step-summary" style={{ marginTop: "1rem" }}>
                      {t("slots.loading")}
                    </p>
                  ) : slots.length === 0 ? (
                    <p className="step-summary" style={{ marginTop: "1rem" }}>
                      {availabilityError || t("slots.none")}
                    </p>
                  ) : (
                    <div className="slot-grid">
                      {slots.map((iso) => (
                        <button
                          type="button"
                          key={iso}
                          onClick={() => setSelectedSlot(iso)}
                          className={`slot-button ${selectedSlot === iso ? "is-selected" : ""}`}
                        >
                          {formatTime(iso)}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="booking-step">
              <div className="step-head">
                <h2>
                  <EmphasisText text={t("step.details")} />
                </h2>
                <span className="booking-step-number">02</span>
              </div>
              <p className="step-summary">{t("details.summary")}</p>
              <div className="address-grid">
                {!user && (
                  <div className="full">
                    <label htmlFor="ln-email">{t("form.email")}</label>
                    <input
                      id="ln-email"
                      type="email"
                      required
                      autoComplete="email"
                      value={guestContact.email}
                      onChange={updateGuestContact("email")}
                      placeholder={t("form.emailPlaceholder")}
                    />
                  </div>
                )}
                {needsBookingPhone && (
                  <div className="full">
                    <label htmlFor="ln-phone">{t("form.phone")}</label>
                    <input
                      id="ln-phone"
                      type="tel"
                      required
                      autoComplete="tel"
                      placeholder={t("form.phonePlaceholder")}
                      value={bookingPhone}
                      onChange={(e) => setBookingPhone(e.target.value)}
                    />
                  </div>
                )}
                <div className="full">
                  <label htmlFor="ln-visit-location">{t("form.visitLocation")}</label>
                  <select
                    id="ln-visit-location"
                    value={guestDetails.visitLocation}
                    onChange={updateVisitLocation}
                  >
                    <option value="hotel">{t("form.visitLocation.hotel")}</option>
                    <option value="wellness">{t("form.visitLocation.wellness")}</option>
                  </select>
                </div>
                {guestDetails.visitLocation !== "wellness" && (
                  <>
                    <div>
                      <label htmlFor="ln-floor">{t("form.floor")}</label>
                      <input
                        id="ln-floor"
                        value={guestDetails.floor}
                        onChange={updateGuest("floor")}
                        placeholder={t("form.floorPlaceholder")}
                      />
                    </div>
                    <div>
                      <label htmlFor="ln-room">{t("form.room")}</label>
                      <input
                        id="ln-room"
                        value={guestDetails.roomNumber}
                        onChange={updateGuest("roomNumber")}
                        placeholder={t("form.roomPlaceholder")}
                      />
                    </div>
                  </>
                )}
                <div className="full">
                  <label htmlFor="ln-notes">{t("form.notes")}</label>
                  <textarea
                    id="ln-notes"
                    rows={3}
                    maxLength={300}
                    value={guestDetails.notes}
                    onChange={updateGuest("notes")}
                    placeholder={t("form.notesPlaceholder")}
                  />
                </div>
              </div>
            </div>

            <div className="booking-step">
              <div className="step-head">
                <h2>
                  <EmphasisText text={t("step.payment")} />
                </h2>
                <span className="booking-step-number">03</span>
              </div>
              <p className="step-summary">{t("payment.summary")}</p>
              <div className="payment-options">
                <label
                  className={`payment-option ${paymentMethod === "on-site" ? "is-selected" : ""}`}
                >
                  <input
                    type="radio"
                    name="ln-payment"
                    value="on-site"
                    checked={paymentMethod === "on-site"}
                    onChange={() => setPaymentMethod("on-site")}
                  />
                  <div className="payment-option-info">
                    <strong>{t("payment.onSite.title")}</strong>
                    <span>{t("payment.onSite.desc")}</span>
                  </div>
                </label>
                <label
                  className={`payment-option ${paymentMethod === "stripe" ? "is-selected" : ""}`}
                >
                  <input
                    type="radio"
                    name="ln-payment"
                    value="stripe"
                    checked={paymentMethod === "stripe"}
                    onChange={() => setPaymentMethod("stripe")}
                  />
                  <div className="payment-option-info">
                    <strong>{t("payment.online.title")}</strong>
                    <span>{t("payment.online.desc")}</span>
                  </div>
                </label>
              </div>
            </div>

            <div className="booking-step">
              <div className="step-head">
                <h2>
                  <EmphasisText text={t("step.booking")} />
                </h2>
                <span className="booking-step-number">04</span>
              </div>
              <div
                className="booking-summary-block"
                style={{ marginTop: 0, borderTop: "none", paddingTop: 0 }}
              >
                <div className="summary-row">
                  <span>{t("summary.service")}</span>
                  <strong>{selectedOption.title}</strong>
                </div>
                <div className="summary-row">
                  <span>{t("summary.duration")}</span>
                  <strong>
                    {t("summary.durationValue", {
                      minutes: selectedOption.durationMinutes,
                    })}
                  </strong>
                </div>
                <div className="summary-row">
                  <span>{t("summary.date")}</span>
                  <strong>
                    {selectedDate ? formatLong(selectedDate) : t("summary.empty")}
                  </strong>
                </div>
                <div className="summary-row">
                  <span>{t("summary.time")}</span>
                  <strong>
                    {selectedSlot ? formatTime(selectedSlot) : t("summary.empty")}
                  </strong>
                </div>
                {bookingGuestName && (
                  <div className="summary-row">
                    <span>{t("summary.name")}</span>
                    <strong>{bookingGuestName}</strong>
                  </div>
                )}
                {bookingGuestContact.email && (
                  <div className="summary-row">
                    <span>{t("summary.email")}</span>
                    <strong>{bookingGuestContact.email}</strong>
                  </div>
                )}
                <div className="summary-row">
                  <span>{t("summary.visitLocation")}</span>
                  <strong>
                    {guestDetails.visitLocation === "wellness"
                      ? t("form.visitLocation.wellness")
                      : t("form.visitLocation.hotel")}
                  </strong>
                </div>
                {guestDetails.visitLocation !== "wellness" && (
                  <>
                    <div className="summary-row">
                      <span>{t("summary.floor")}</span>
                      <strong>{guestDetails.floor || t("summary.empty")}</strong>
                    </div>
                    <div className="summary-row">
                      <span>{t("summary.room")}</span>
                      <strong>{guestDetails.roomNumber || t("summary.empty")}</strong>
                    </div>
                  </>
                )}
                <div className="summary-row">
                  <span>{t("summary.payment")}</span>
                  <strong>
                    {paymentMethod === "stripe"
                      ? t("summary.paymentOnline")
                      : t("summary.paymentOnSite")}
                  </strong>
                </div>
                <div className="total">
                  <span>{t("summary.total")}</span>
                  <strong>{formatPrice(effectivePriceCents)}</strong>
                </div>
              </div>

              {error && (
                <div className="auth-alert is-error" style={{ marginTop: "1rem" }}>
                  {error}
                </div>
              )}

              {awaitingEmailVerification && (
                <div className="leonardo-email-notice auth-alert is-info">
                  <strong>{t("verify.title")}</strong>
                  <p>{t("verify.body")}</p>
                </div>
              )}

              <div className="booking-actions leonardo-booking-actions">
                <button
                  type="button"
                  className="btn-primary"
                  disabled={submitting || !formComplete}
                  onClick={handleSubmitClick}
                >
                  {submitLabel}
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </Layout>
  );
};

export default LeonardoNyxLanding;
